/**
 * User-facing titles, explanations, and recommended actions for each {@link ErrorCode}.
 * Keep in sync with BFF `ErrorCode` semantics where codes originate from the backend.
 */
import { ErrorCode } from '@/errors/codes'

export interface ErrorPageUserMessages {
  // One error page’s user-visible strings (main heading, body, optional numbered actions).

  title: string
  // Main heading (e.g. "Oops! You are not authorized!").

  explanation: string
  // Default explanation when the page does not override it from the route query.

  actions?: readonly string[]
  // Numbered “Recommended actions” before the final error-code line. When omitted, DEFAULT_AUTH_ERROR_ACTIONS is used for auth-style pages.
}

/** Default list for OAuth and most 401 flows (matches prior AuthErrorView behavior). */
export const DEFAULT_AUTH_ERROR_ACTIONS: readonly string[] = [
  'Try logging in again.',
  'If the problem persists, please contact the administrator.',
]

const CREDENTIALS_ACTIONS: readonly string[] = [
  'Please check your credentials and try logging in again.',
  'If the problem persists, please contact the administrator.',
]

const NOT_FOUND_ACTIONS: readonly string[] = [
  'Please check correctness of the URL in the address bar.',
  'If the URL is correct, please contact the administrator.',
]

const RETRY_CONTACT_ACTIONS: readonly string[] = [
  'Please try again.',
  'If the problem persists, please contact the administrator.',
]

/**
 * Static user messages for every application error code. Dynamic fragments (BFF message, `?from=`) are merged
 * via helpers below, not embedded here.
 */
export const ERROR_USER_MESSAGES: Record<ErrorCode, ErrorPageUserMessages> = {
  [ErrorCode.Unauthorized]: {
    title: 'Oops! You are not authorized!',
    explanation: 'You are not authorized. Please login.',
    actions: CREDENTIALS_ACTIONS,
  },
  [ErrorCode.AuthCodeTokenExchangeFailed]: {
    title: 'Oops! Authorization code exchange failed!',
    explanation:
      'Keycloak did not return OAuth tokens after exchanging the authorization code. The sign-in flow could not be completed.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakTokenSignatureInvalid]: {
    title: 'Oops! Token signature verification failed!',
    explanation:
      'The access, ID, or refresh token from Keycloak could not be verified (wrong signing key or corrupted token).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakTokenExpirationInvalid]: {
    title: 'Oops! Token expiration is invalid!',
    explanation: 'A token from Keycloak is past its expiration time or failed expiration checks.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakJwtDecodeFailed]: {
    title: 'Oops! Could not decode the token!',
    explanation:
      'A token returned from Keycloak could not be read (malformed JWT or failed validation inside the token library).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UnknownErrorDuringJwtDecoding]: {
    title: 'Oops! Token claims validation failed!',
    explanation:
      'After decoding the token, required claims did not match the expected shape (missing fields or unexpected values).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakIdTokenNonceViolation]: {
    title: 'Oops! Login session validation failed!',
    explanation:
      'The ID token nonce did not match the value from the start of sign-in (possible tampering or replay of the login flow).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakIntrospectionFailed]: {
    title: 'Oops! Token introspection failed!',
    explanation:
      'The server could not confirm the token with Keycloak (network error, invalid response, or token not active).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XauthDataCookieTooLarge]: {
    title: 'Oops! Session data is too large!',
    explanation:
      'The encrypted session cookie would exceed the browser size limit, so the server could not store your session tokens safely. Try signing in again; if this keeps happening, contact the administrator.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakUserinfoTimeout]: {
    title: 'Oops! User profile request timed out!',
    explanation:
      'The server timed out while asking Keycloak for your user profile after sign-in (no response in time).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakUserinfoHttpError]: {
    title: 'Oops! User profile request failed!',
    explanation:
      'Keycloak returned an error when the server asked for your user profile with your access token (for example access denied or a server error).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.KeycloakUserinfoUnknownError]: {
    title: 'Oops! User profile request failed!',
    explanation:
      'Something went wrong while the server was asking Keycloak for your user profile (for example a connection problem before a complete response).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoMissingContact]: {
    title: 'Oops! Contact information is missing!',
    explanation:
      'Your identity profile does not include an email address or phone number. At least one contact channel is required to continue.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoMissingName]: {
    title: 'Oops! Name information is missing!',
    explanation:
      'Your identity profile is missing the required first name and/or last name expected by the application.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoMissingGender]: {
    title: 'Oops! Gender is missing!',
    explanation: 'Your identity profile does not include the required gender field.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoInvalidGender]: {
    title: 'Oops! Gender value is not supported!',
    explanation:
      'The gender value in your identity profile is not one of the values supported by the application.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoMissingAddress]: {
    title: 'Oops! Address is missing!',
    explanation: 'Your identity profile does not include the required address information.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoValidation]: {
    title: 'Oops! Profile data could not be validated!',
    explanation:
      'Some fields in your user profile from the identity server did not pass validation (for example invalid format or unexpected values).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoUnknownGroup]: {
    title: 'Oops! Unknown group in your profile!',
    explanation:
      'Your identity profile lists at least one group path that this application does not recognize (it may be outdated or misconfigured in the realm).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.UserinfoUnknownRole]: {
    title: 'Oops! Unknown role in your profile!',
    explanation:
      'Your identity profile lists at least one client role that this application does not recognize (it may be outdated or misconfigured for the Plyas client).',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XsrfTokenCookieMissing]: {
    title: 'Oops! Session security token is missing!',
    explanation:
      'The server did not receive the expected CSRF cookie for this request. You may need to sign in again so a new session (and cookie) can be established.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.NoCsrfHeader]: {
    title: 'Oops! Request could not be verified!',
    explanation:
      'The server expected a security header that matches your session cookie, but it was not sent. Try refreshing the page or signing in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.CsrfTokenMismatch]: {
    title: 'Oops! Security token mismatch!',
    explanation:
      'The security header and session cookie did not match. Your session may be stale; try signing in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XauthDataCookieDecryptFailed]: {
    title: 'Oops! Session cookie could not be decrypted!',
    explanation:
      'The server could not decode or decrypt your XAUTH-DATA session cookie (for example corrupted cookie value, wrong key, or invalid authentication tag). Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XauthDataCookieDecompressFailed]: {
    title: 'Oops! Session cookie payload is invalid!',
    explanation:
      'The decrypted XAUTH-DATA payload could not be decompressed as an LZ4 frame (for example malformed or truncated compressed payload). Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XauthDataCookieDeserializeFailed]: {
    title: 'Oops! Session data format is invalid!',
    explanation:
      'The decompressed XAUTH-DATA payload could not be parsed into the expected token schema (invalid JSON or missing/invalid token fields). Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.XauthDataCookieUnknownError]: {
    title: 'Oops! Session processing failed!',
    explanation:
      'An unexpected error occurred while processing the XAUTH-DATA session cookie after decoding or decryption started and before a valid token bundle could be loaded. Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.AccessTokenMissing]: {
    title: 'Oops! Session token is missing!',
    explanation:
      'The server reached a protected endpoint but could not load your access token from the session (the XAUTH-DATA cookie is missing or could not be parsed). Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.SessionNotAuthenticated]: {
    title: 'Oops! Your session is not authenticated!',
    explanation:
      'A session-only endpoint was called without an XAUTH-DATA session cookie on the request, so no signed-in user could be resolved. Please sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.AccessTokenRefreshFailed]: {
    title: 'Oops! Your session has expired!',
    explanation:
      'Your access token expired and the server could not obtain a new one from Keycloak using the stored refresh token (the refresh token may have expired or been invalidated, or Keycloak did not respond successfully). Your session has ended and you need to sign in again.',
    actions: DEFAULT_AUTH_ERROR_ACTIONS,
  },
  [ErrorCode.PermissionDenied]: {
    title: 'Oops! You do not have permission!',
    explanation:
      'You are signed in, but your Keycloak group memberships do not grant every permission required for this action.',
    actions: [
      'If you need access, ask your administrator to assign the correct Keycloak groups or roles.',
      'If permissions were updated recently, try signing out and signing in again so your session reflects the new memberships.',
    ],
  },
  [ErrorCode.ClientsByIdsNoClientsFound]: {
    title: 'Oops! No matching clients found!',
    explanation:
      'The request looked up clients by id, but none of the supplied ids belong to a person with the client role (the ids may not exist, or the persons they point to are not clients).',
    actions: RETRY_CONTACT_ACTIONS,
  },
  [ErrorCode.NoFragmentInAuthResponse]: {
    title: 'Oops! Invalid auth response!',
    explanation:
      'The authorization server did not return the expected data in the response (for example code or state in the URL).',
  },
  [ErrorCode.NoCodeInAuthResponse]: {
    title: 'Oops! No authorization code!',
    explanation: 'The authorization server did not return an authorization code in the response.',
  },
  [ErrorCode.NoStateInAuthResponse]: {
    title: 'Oops! No state in auth response!',
    explanation:
      'The authorization server did not return the state parameter in the response. The login may have been tampered with.',
  },
  [ErrorCode.InvalidStateInAuthResponse]: {
    title: 'Oops! Invalid state!',
    explanation:
      'The state returned by the authorization server does not match. The login response may have been tampered with or replayed.',
  },
  [ErrorCode.NoSessionStateInAuthResponse]: {
    title: 'Oops! No session state in auth response!',
    explanation:
      'The authorization server did not return a session_state value in the response. The login session could not be bound to a server-side session.',
  },
  [ErrorCode.NoIssInAuthResponse]: {
    title: 'Oops! No issuer in auth response!',
    explanation:
      'The authorization server did not return an issuer (iss) value in the response. The login response cannot be validated against the expected realm.',
  },
  [ErrorCode.InvalidIssInAuthResponse]: {
    title: 'Oops! Invalid issuer!',
    explanation:
      'The issuer (iss) in the authorization response does not match the expected realm. The login response may have come from a different or misconfigured identity provider.',
  },
  [ErrorCode.NoNonceInStorage]: {
    title: 'Oops! Missing login nonce!',
    explanation:
      'The browser had no OAuth nonce from the start of sign-in. Try logging in again from the welcome page.',
  },
  [ErrorCode.NotFound]: {
    title: 'Oops! The page not found!',
    explanation: 'The page you are looking for does not exist or the address is wrong.',
    actions: NOT_FOUND_ACTIONS,
  },
  [ErrorCode.KeycloakUnavailable]: {
    title: 'Oops! Authorization server is currently unavailable!',
    explanation: 'Authorization server is currently unavailable.',
    actions: RETRY_CONTACT_ACTIONS,
  },
}

/** Unauthorized: optional `backendMessage` query augments the default explanation. */
export function unauthorizedExplanation(backendMessage: unknown): string {
  const base = ERROR_USER_MESSAGES[ErrorCode.Unauthorized].explanation
  if (typeof backendMessage === 'string' && backendMessage.trim().length > 0) {
    return `${backendMessage} Please provide this information to support.`
  }
  return base
}

/** NotFound: optional `from` query names the path that was not found. */
export function notFoundExplanation(fromPath: unknown): string {
  if (typeof fromPath === 'string' && fromPath.length > 0) {
    return `You are trying to access ${fromPath}. This page does not exist or the address is wrong.`
  }
  return ERROR_USER_MESSAGES[ErrorCode.NotFound].explanation
}

/**
 * For BFF-driven error pages: use the backend-supplied message (e.g. from the `errorMessage` route query)
 * when non-empty; otherwise fall back to the static {@link ERROR_USER_MESSAGES} explanation for `code`.
 */
export function errorExplanationFromBackend(code: ErrorCode, backendErrorMessage: unknown): string {
  if (typeof backendErrorMessage === 'string' && backendErrorMessage.trim().length > 0) {
    return backendErrorMessage
  }
  return ERROR_USER_MESSAGES[code].explanation
}
